import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Form, Input, message, Spin } from 'antd';
import axios from 'axios';
import GenericTable from './GenericTable';
import './OrgConfigurables.css';

const OrgConfigurables = ({ orgId }) => {
    const [configurables, setConfigurables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        if (!orgId) return;
        fetchConfigurables();
    }, [orgId]);

    const fetchConfigurables = async (search = '') => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ orgId });
            if (search) {
                params.append('search', search);
            }

            const response = await axios.get(`${process.env.REACT_APP_API}/api/configurables?${params.toString()}`);

            const payload = response.data;
            const list = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.data)
                    ? payload.data
                    : Array.isArray(payload?.data?.data)
                        ? payload.data.data
                        : [];

            setConfigurables(list);
        } catch (error) {
            console.error('Error fetching configurables:', error);
            message.error(error?.response?.data?.message || 'Failed to load configurables');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        fetchConfigurables(value);
    };

    const openEditDrawer = (config) => {
        setSelectedConfig(config);
        form.setFieldsValue({ value: config?.value || '' });
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedConfig(null);
        form.resetFields();
    };

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();

            await axios.patch(
                `${process.env.REACT_APP_API}/api/configurables/${encodeURIComponent(selectedConfig.key)}`,
                { orgId, value: values.value }
            );

            message.success('Configurable updated successfully');
            closeDrawer();
            fetchConfigurables();
        } catch (error) {
            console.error('Error updating configurable:', error);
            message.error('Failed to update configurable');
        }
    };

    const tableData = useMemo(() => {
        return (configurables || []).map((item) => ({
            ...item,
            id: `${item.key}-${item.updatedAt || ''}`,
        }));
    }, [configurables]);

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
    }

    return (
        <div className="org-configurables">
            <div className="org-configurables__header">
                <h3>Configurables</h3>
            </div>

            <GenericTable
                data={tableData}
                sortableColumns={['key', 'value']}
                searchInput=""
                onSearch={handleSearch}
                actions={['edit']}
                actionFunctions={[openEditDrawer]}
            />

            <Drawer
                title={`Edit Configurable${selectedConfig ? `: ${selectedConfig.key}` : ''}`}
                open={isDrawerOpen}
                onClose={closeDrawer}
                width={480}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={handleUpdate}>
                            Save
                        </Button>
                    </div>
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item label="Key">
                        <Input value={selectedConfig?.key || ''} readOnly />
                    </Form.Item>
                    <Form.Item
                        name="value"
                        label="Value"
                        rules={[{ required: true, message: 'Value is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default OrgConfigurables;
